import { Injectable, Inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';
import { Router } from '@angular/router';

export interface JobPostingSchema {
  titulo: string;
  empresa: string;
  empresaUrl?: string;
  descripcion: string;
  descripcionCorta?: string;
  fechaPublicacion: string;
  fechaExpiracion?: string;
  modalidad: string;       // REMOTO | HIBRIDO | PRESENCIAL
  ciudad?: string;
  salarioMin?: number;
  salarioMax?: number;
  slug: string;
  logo?: string;
  tecnologias?: string[];  // para keywords dinámicas
}

@Injectable({ providedIn: 'root' })
export class SeoService {

  private readonly siteUrl = 'https://empleos.tecgurus.net';
  private readonly siteName = 'TecGurus Empleos';
  private readonly defaultDescription =
    'Encuentra las mejores vacantes de tecnología en México. Empleos para desarrolladores, '
    + 'ingenieros de software, QA, DevOps y más — con match de IA.';

  constructor(
    private meta: Meta,
    private titleService: Title,
    private router: Router,
    @Inject(DOCUMENT) private doc: Document
  ) {}

  /** Página genérica (home, listado, etc.) */
  setDefault(pageTitle?: string, description?: string) {
    const title = pageTitle
      ? `${pageTitle} | ${this.siteName}`
      : `${this.siteName} — Empleos Tech con IA`;

    this.titleService.setTitle(title);
    this.setCommonMeta(title, description ?? this.defaultDescription);
    this.removeJobPostingScript();
  }

  /**
   * Vacante cerrada — meta noindex,follow para que Google la deindexe
   * pero siga los links a vacantes similares (preserva autoridad SEO).
   */
  setNoIndex() {
    this.meta.updateTag({ name: 'robots', content: 'noindex,follow' });
    this.removeJobPostingScript();
  }

  /** Detalle de vacante — inyecta meta tags + JSON-LD JobPosting */
  setJobPosting(job: JobPostingSchema) {
    const title = `${job.titulo} en ${job.empresa} | ${this.siteName}`;
    // Strip HTML del scraper antes de usar como meta description
    const cleanDesc = job.descripcion
      ? job.descripcion.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
      : '';
    const desc = job.descripcionCorta?.replace(/<[^>]+>/g, ' ').trim()
      ?? cleanDesc.substring(0, 160);
    const url   = `${this.siteUrl}/vacantes/detalles/${job.slug}`;
    const ogImg = job.logo ?? `${this.siteUrl}/assets/img/sitio-web-de-tg.jpg`;

    this.titleService.setTitle(title);
    this.setCommonMeta(title, desc, url, ogImg, job);
    this.injectJobPostingScript(job, url);
  }

  // ─────────────────────────────────────────────────────────────────────────

  private setCommonMeta(title: string, description: string, url?: string, image?: string, job?: JobPostingSchema) {
    const canonicalUrl = url ?? `${this.siteUrl}${this.router.url}`;
    const ogImg = image ?? `${this.siteUrl}/assets/img/sitio-web-de-tg.jpg`;

    // Keywords dinámicas: tecnologías + rol + empresa + ciudad cuando es vacante
    const keywords = job
      ? [
          ...(job.tecnologias ?? []),
          job.titulo, job.empresa,
          job.ciudad ?? 'México',
          'vacante tech', 'empleo tecnología', 'TecGurus Empleos',
        ].filter(Boolean).join(', ')
      : 'empleos, tecnología, programador, vacantes tech, desarrollo de software, tec gurus, bolsa de trabajo';

    // Standard
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ name: 'keywords',    content: keywords });

    // Open Graph
    this.meta.updateTag({ property: 'og:title',            content: title });
    this.meta.updateTag({ property: 'og:description',      content: description });
    this.meta.updateTag({ property: 'og:url',              content: canonicalUrl });
    this.meta.updateTag({ property: 'og:site_name',        content: this.siteName });
    this.meta.updateTag({ property: 'og:type',             content: job ? 'article' : 'website' });
    this.meta.updateTag({ property: 'og:image',            content: ogImg });
    if (job) {
      this.meta.updateTag({ property: 'article:published_time', content: job.fechaPublicacion });
      this.meta.updateTag({ property: 'article:section',        content: 'Empleos' });
    }

    // Twitter Card
    this.meta.updateTag({ name: 'twitter:card',        content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title',       content: title });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.meta.updateTag({ name: 'twitter:image',       content: ogImg });

    // Canonical
    this.setCanonical(canonicalUrl);
  }

  private setCanonical(url: string) {
    let link: HTMLLinkElement = this.doc.querySelector("link[rel='canonical']") as HTMLLinkElement;
    if (!link) {
      link = this.doc.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.doc.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }

  private injectJobPostingScript(job: JobPostingSchema, url: string) {
    this.removeJobPostingScript();

    const salaryBlock = (job.salarioMin && job.salarioMax) ? {
      baseSalary: {
        '@type': 'MonetaryAmount',
        currency: 'MXN',
        value: {
          '@type': 'QuantitativeValue',
          minValue: job.salarioMin,
          maxValue: job.salarioMax,
          unitText: 'MONTH',
        },
      }
    } : {};

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'JobPosting',
      title: job.titulo,
      description: job.descripcion,
      datePosted: job.fechaPublicacion,
      ...(job.fechaExpiracion ? { validThrough: job.fechaExpiracion } : {}),
      employmentType: this.mapModalidad(job.modalidad),
      hiringOrganization: {
        '@type': 'Organization',
        name: job.empresa,
        ...(job.empresaUrl ? { sameAs: job.empresaUrl } : {}),
      },
      jobLocation: {
        '@type': 'Place',
        address: {
          '@type': 'PostalAddress',
          addressLocality: job.ciudad ?? 'México',
          addressCountry: 'MX',
        },
      },
      ...(job.modalidad?.toUpperCase().includes('REMOTO') ? { jobLocationType: 'TELECOMMUTE' } : {}),
      url,
      ...salaryBlock,
    };

    const script = this.doc.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'job-posting-schema';
    script.text = JSON.stringify(schema);
    this.doc.head.appendChild(script);
  }

  private removeJobPostingScript() {
    const el = this.doc.getElementById('job-posting-schema');
    if (el) el.remove();
  }

  private mapModalidad(modalidad: string): string {
    const m = modalidad?.toUpperCase() ?? '';
    if (m.includes('REMOTO'))    return 'TELECOMMUTE';
    if (m.includes('PARCIAL'))   return 'PART_TIME';
    if (m.includes('TEMPORAL'))  return 'TEMPORARY';
    if (m.includes('CONTRATO'))  return 'CONTRACTOR';
    return 'FULL_TIME';
  }
}
